import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAnnualProjectPrograms } from '../services/annualProjectProgramsService';
import type {
  AcademicTerm,
  AnnualTimetableCell,
  CalendarDay,
  DayKind,
  Grade,
  ProgramFixedContentRow,
  ProgramProgressCell,
  ProgramScopedContentRow,
  Track,
  WeeklyLearningGuideNote,
} from '../types/domain';
import { buildAnnualClassEntries } from '../utils/annualTimetable';
import type { BaseTimetableByTerm, BaseTimetableGrid, ClassScheduleSettings } from '../services/sectionLayoutService';
import { normalizeBaseTimetableByTerm } from '../services/sectionLayoutService';
import type { OffDayByDate } from '../utils/annualProgressDataCache';
import {
  AnnualProjectProgram,
  hasAnnualProjectProgramsCacheEntry,
  loadAnnualProjectPrograms,
} from '../utils/annualProjectProgramsStorage';
import { consumePostLoginSchoolDataNetworkRefresh } from '../utils/postLoginSchoolDataRefresh';
import { useAnnualProgressMetaQuery } from '../query/hooks/useAnnualProgressMetaQuery';
import { useAnnualTimetableCellsQuery } from '../query/hooks/useAnnualTimetableCellsQuery';
import { useCalendarDaysQuery } from '../query/hooks/useCalendarDaysQuery';
import { useProgramFixedRowsQuery } from '../query/hooks/useProgramFixedRowsQuery';
import { useProgramProgressCellsQuery } from '../query/hooks/useProgramProgressCellsQuery';
import { useProgramScopedRowsQuery } from '../query/hooks/useProgramScopedRowsQuery';
import { useWeeklyLearningGuideNotesQuery } from '../query/hooks/useWeeklyLearningGuideNotesQuery';
import { useWeeklyLearningGuideTrackColorsQuery } from '../query/hooks/useWeeklyLearningGuideTrackColorsQuery';
import { lsInitialDataOptions } from '../query/hooks/lsInitialData';
import { schoolKeys } from '../query/keys';
import { deviceCacheStorage } from '../utils/deviceCacheStorage';

type TermNumber = 1 | 2;
type TermMap<T> = { 1: T; 2: T };

const PROGRAM_COLOR_SETTINGS_LS_PREFIX = 'cp.programColorSettings.v2.';

function loadBaseTimetableByGradeFromLs(workspaceId: string): Record<string, BaseTimetableByTerm> {
  if (!workspaceId || typeof window === 'undefined') return {};
  try {
    const raw = deviceCacheStorage.getItem(`${PROGRAM_COLOR_SETTINGS_LS_PREFIX}${workspaceId}`);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as { baseTimetableByGrade?: Record<string, unknown> };
    const rawMap = parsed.baseTimetableByGrade;
    if (!rawMap || typeof rawMap !== 'object') return {};
    const out: Record<string, BaseTimetableByTerm> = {};
    Object.keys(rawMap).forEach((gradeId) => {
      out[gradeId] = normalizeBaseTimetableByTerm(rawMap[gradeId]);
    });
    return out;
  } catch {
    return {};
  }
}

function calendarDaysToOffDayState(days: CalendarDay[]): {
  holidayDays: string[];
  offDayByDate: OffDayByDate;
} {
  const offDayByDate: OffDayByDate = {};
  const holidayDays: string[] = [];
  for (const d of days) {
    if (d.day_kind === 'holiday' || d.day_kind === 'closure' || d.day_kind === 'vacation') {
      holidayDays.push(d.day);
      const note = (d.note ?? '').trim();
      offDayByDate[d.day] = note ? { kind: d.day_kind, note } : { kind: d.day_kind };
    }
  }
  return { holidayDays, offDayByDate };
}

function useWeeklyLearningGuideTermBundle(params: {
  workspaceId: string;
  academicYear: number;
  gradeId: string;
  termNumber: TermNumber;
  termDef: AcademicTerm | null;
  enabled: boolean;
  forceNetwork: boolean;
  useLinkedCalendarDays: boolean;
}) {
  const { workspaceId, academicYear, gradeId, termNumber, termDef, enabled, forceNetwork, useLinkedCalendarDays } =
    params;

  const timetableQuery = useAnnualTimetableCellsQuery({
    workspaceId,
    academicYear,
    termNumber,
    gradeId,
    enabled,
    forceNetwork,
  });

  const progressQuery = useProgramProgressCellsQuery({
    workspaceId,
    academicYear,
    termNumber,
    gradeId,
    enabled,
    forceRefresh: forceNetwork,
  });

  const scopedRowsQuery = useProgramScopedRowsQuery({
    workspaceId,
    academicYear,
    termNumber,
    gradeId,
    enabled,
    forceRefresh: forceNetwork,
  });

  const fixedRowsQuery = useProgramFixedRowsQuery({
    workspaceId,
    academicYear,
    termNumber,
    gradeId,
    enabled,
    forceRefresh: forceNetwork,
  });

  const calendarQuery = useCalendarDaysQuery({
    workspaceId,
    from: termDef?.starts_on ?? '',
    to: termDef?.ends_on ?? '',
    enabled: enabled && !useLinkedCalendarDays && !!termDef?.starts_on && !!termDef?.ends_on,
  });

  const notesQuery = useWeeklyLearningGuideNotesQuery({
    workspaceId,
    academicYear,
    termNumber,
    gradeId,
    enabled,
    forceRefresh: forceNetwork,
  });

  return {
    timetableQuery,
    progressQuery,
    scopedRowsQuery,
    fixedRowsQuery,
    calendarQuery,
    notesQuery,
  };
}

export type UseWeeklyLearningGuidePageQueriesParams = {
  workspaceId: string;
  gradeId: string;
  grades: Grade[];
  academicYear: number;
  loadTermNumbers: TermNumber[];
  schoolScopedCacheSyncReady: boolean;
  enabled?: boolean;
  /** 학사일정 섹션이 로컬 일정을 쓰는 클래스면 그 목록(대시보드 위클리와 동일 소스). null이면 DB `calendar_days` */
  linkedCalendarDays?: CalendarDay[] | null;
};

export type UseWeeklyLearningGuidePageQueriesResult = {
  terms: AcademicTerm[];
  schedule: ReturnType<typeof buildAnnualClassEntries>[number] | null;
  classScheduleSettings: ClassScheduleSettings | null;
  tracks: Track[];
  regularColors: string[];
  dedicatedColors: string[];
  baseExcludeColors: string[];
  baseTimetableByGrade: Record<string, BaseTimetableByTerm>;
  annualProjectPrograms: AnnualProjectProgram[];
  annualRowsByTerm: TermMap<AnnualTimetableCell[]>;
  progressRowsByTerm: TermMap<ProgramProgressCell[]>;
  calendarDaysByTerm: TermMap<CalendarDay[]>;
  savedNotesByTerm: TermMap<WeeklyLearningGuideNote[]>;
  scopedRowsByTerm: TermMap<ProgramScopedContentRow[]>;
  fixedContentRowsByTerm: TermMap<ProgramFixedContentRow[]>;
  savedTrackColorMap: Record<string, string>;
  nonClassHolidaySetByTerm: TermMap<Set<string>>;
  calendarDayKindByTerm: TermMap<Record<string, DayKind>>;
  offDayByDateByTerm: TermMap<OffDayByDate>;
  loading: boolean;
  notesLoading: boolean;
  error: string | null;
};

export function useWeeklyLearningGuidePageQueries(
  params: UseWeeklyLearningGuidePageQueriesParams
): UseWeeklyLearningGuidePageQueriesResult {
  const {
    workspaceId,
    gradeId,
    grades,
    academicYear,
    loadTermNumbers,
    schoolScopedCacheSyncReady,
    enabled = true,
    linkedCalendarDays = null,
  } = params;
  const useLinkedCalendarDays = linkedCalendarDays != null;

  const loadTermSet = useMemo(() => new Set(loadTermNumbers), [loadTermNumbers]);
  const queryEnabled = enabled && schoolScopedCacheSyncReady && !!workspaceId.trim() && !!gradeId.trim();
  const [forceNetwork, setForceNetwork] = useState(() => consumePostLoginSchoolDataNetworkRefresh(workspaceId));

  useEffect(() => {
    if (consumePostLoginSchoolDataNetworkRefresh(workspaceId)) {
      setForceNetwork(true);
    }
  }, [workspaceId]);

  const metaQuery = useAnnualProgressMetaQuery({
    workspaceId,
    academicYear,
    termNumber: 1,
    gradeId,
    enabled: queryEnabled,
  });

  const terms: AcademicTerm[] = metaQuery.data?.terms ?? [];
  const termByNumber = useMemo(
    () => ({
      1: terms.find((term) => term.term_number === 1) ?? null,
      2: terms.find((term) => term.term_number === 2) ?? null,
    }),
    [terms]
  );

  const term1Bundle = useWeeklyLearningGuideTermBundle({
    workspaceId,
    academicYear,
    gradeId,
    termNumber: 1,
    termDef: termByNumber[1],
    enabled: queryEnabled && loadTermSet.has(1),
    forceNetwork,
    useLinkedCalendarDays,
  });

  const term2Bundle = useWeeklyLearningGuideTermBundle({
    workspaceId,
    academicYear,
    gradeId,
    termNumber: 2,
    termDef: termByNumber[2],
    enabled: queryEnabled && loadTermSet.has(2),
    forceNetwork,
    useLinkedCalendarDays,
  });

  const projectProgramsLs = loadAnnualProjectPrograms(workspaceId, gradeId, academicYear);
  const projectProgramsLsFound = hasAnnualProjectProgramsCacheEntry(workspaceId, gradeId, academicYear);
  const annualProjectProgramsQuery = useQuery({
    queryKey: schoolKeys.annualProjectPrograms(workspaceId, academicYear, gradeId),
    enabled: queryEnabled,
    ...lsInitialDataOptions(projectProgramsLsFound, projectProgramsLs),
    queryFn: () => getAnnualProjectPrograms(workspaceId, gradeId, academicYear),
  });

  const trackColorsQuery = useWeeklyLearningGuideTrackColorsQuery({
    workspaceId,
    academicYear,
    gradeId,
    enabled: queryEnabled,
    forceRefresh: forceNetwork,
  });

  useEffect(() => {
    if (!forceNetwork || !queryEnabled || !metaQuery.isSuccess || !annualProjectProgramsQuery.isSuccess || !trackColorsQuery.isSuccess) {
      return;
    }
    const term1Ready =
      !loadTermSet.has(1) ||
      (term1Bundle.timetableQuery.isSuccess &&
        term1Bundle.progressQuery.isSuccess &&
        term1Bundle.scopedRowsQuery.isSuccess &&
        term1Bundle.fixedRowsQuery.isSuccess &&
        term1Bundle.notesQuery.isSuccess &&
        (useLinkedCalendarDays ||
          !termByNumber[1]?.starts_on ||
          !termByNumber[1]?.ends_on ||
          term1Bundle.calendarQuery.isSuccess));
    const term2Ready =
      !loadTermSet.has(2) ||
      (term2Bundle.timetableQuery.isSuccess &&
        term2Bundle.progressQuery.isSuccess &&
        term2Bundle.scopedRowsQuery.isSuccess &&
        term2Bundle.fixedRowsQuery.isSuccess &&
        term2Bundle.notesQuery.isSuccess &&
        (useLinkedCalendarDays ||
          !termByNumber[2]?.starts_on ||
          !termByNumber[2]?.ends_on ||
          term2Bundle.calendarQuery.isSuccess));
    if (term1Ready && term2Ready) {
      setForceNetwork(false);
    }
  }, [
    annualProjectProgramsQuery.isSuccess,
    forceNetwork,
    loadTermSet,
    metaQuery.isSuccess,
    queryEnabled,
    term1Bundle,
    term2Bundle,
    termByNumber,
    trackColorsQuery.isSuccess,
    useLinkedCalendarDays,
  ]);

  const schedule = useMemo(() => {
    if (!metaQuery.data?.schedule) return null;
    return (
      buildAnnualClassEntries(metaQuery.data.schedule, grades).find((entry) => entry.gradeId === gradeId) ?? null
    );
  }, [gradeId, grades, metaQuery.data?.schedule]);

  const linkedCalendarDaysByTerm = useMemo<TermMap<CalendarDay[]> | null>(() => {
    if (!linkedCalendarDays) return null;
    const inTerm = (term: AcademicTerm | null) =>
      term?.starts_on && term.ends_on
        ? linkedCalendarDays.filter((d) => d.day >= term.starts_on && d.day <= term.ends_on)
        : [];
    return { 1: inTerm(termByNumber[1]), 2: inTerm(termByNumber[2]) };
  }, [linkedCalendarDays, termByNumber]);

  const term1CalendarData = term1Bundle.calendarQuery.data;
  const term2CalendarData = term2Bundle.calendarQuery.data;
  const calendarDaysByTerm = useMemo<TermMap<CalendarDay[]>>(
    () => linkedCalendarDaysByTerm ?? { 1: term1CalendarData ?? [], 2: term2CalendarData ?? [] },
    [linkedCalendarDaysByTerm, term1CalendarData, term2CalendarData]
  );

  const nonClassHolidaySetByTerm = useMemo<TermMap<Set<string>>>(
    () => ({
      1: new Set(
        calendarDaysByTerm[1]
          .filter((row) => row.day_kind === 'holiday' || row.day_kind === 'closure' || row.day_kind === 'vacation')
          .map((row) => row.day)
      ),
      2: new Set(
        calendarDaysByTerm[2]
          .filter((row) => row.day_kind === 'holiday' || row.day_kind === 'closure' || row.day_kind === 'vacation')
          .map((row) => row.day)
      ),
    }),
    [calendarDaysByTerm]
  );

  const calendarDayKindByTerm = useMemo<TermMap<Record<string, DayKind>>>(
    () => ({
      1: Object.fromEntries(calendarDaysByTerm[1].map((d) => [d.day, d.day_kind])) as Record<string, DayKind>,
      2: Object.fromEntries(calendarDaysByTerm[2].map((d) => [d.day, d.day_kind])) as Record<string, DayKind>,
    }),
    [calendarDaysByTerm]
  );

  const offDayByDateByTerm = useMemo<TermMap<OffDayByDate>>(
    () => ({
      1: calendarDaysToOffDayState(calendarDaysByTerm[1]).offDayByDate,
      2: calendarDaysToOffDayState(calendarDaysByTerm[2]).offDayByDate,
    }),
    [calendarDaysByTerm]
  );

  const loading =
    queryEnabled &&
    (metaQuery.isPending ||
      annualProjectProgramsQuery.isPending ||
      trackColorsQuery.isPending ||
      (loadTermSet.has(1) &&
        (term1Bundle.timetableQuery.isPending ||
          term1Bundle.progressQuery.isPending ||
          term1Bundle.scopedRowsQuery.isPending ||
          term1Bundle.fixedRowsQuery.isPending ||
          (!useLinkedCalendarDays && term1Bundle.calendarQuery.isPending))) ||
      (loadTermSet.has(2) &&
        (term2Bundle.timetableQuery.isPending ||
          term2Bundle.progressQuery.isPending ||
          term2Bundle.scopedRowsQuery.isPending ||
          term2Bundle.fixedRowsQuery.isPending ||
          (!useLinkedCalendarDays && term2Bundle.calendarQuery.isPending))));

  /** 가정통신(주간 메모)은 시간표 본문과 따로 로드 — 표는 먼저 그리고 문구는 도착 후 채움 */
  const notesLoading =
    queryEnabled &&
    ((loadTermSet.has(1) && term1Bundle.notesQuery.isPending) ||
      (loadTermSet.has(2) && term2Bundle.notesQuery.isPending));

  const error =
    metaQuery.error?.message ??
    annualProjectProgramsQuery.error?.message ??
    trackColorsQuery.error?.message ??
    (loadTermSet.has(1)
      ? term1Bundle.timetableQuery.error?.message ??
        term1Bundle.progressQuery.error?.message ??
        term1Bundle.scopedRowsQuery.error?.message ??
        term1Bundle.fixedRowsQuery.error?.message ??
        term1Bundle.notesQuery.error?.message ??
        term1Bundle.calendarQuery.error?.message
      : null) ??
    (loadTermSet.has(2)
      ? term2Bundle.timetableQuery.error?.message ??
        term2Bundle.progressQuery.error?.message ??
        term2Bundle.scopedRowsQuery.error?.message ??
        term2Bundle.fixedRowsQuery.error?.message ??
        term2Bundle.notesQuery.error?.message ??
        term2Bundle.calendarQuery.error?.message
      : null) ??
    null;

  const baseTimetableByGrade = useMemo(
    () => loadBaseTimetableByGradeFromLs(workspaceId),
    [workspaceId, metaQuery.dataUpdatedAt]
  );

  return {
    terms,
    schedule,
    classScheduleSettings: metaQuery.data?.schedule ?? null,
    tracks: metaQuery.data?.tracks ?? [],
    regularColors: metaQuery.data?.regularColors ?? [],
    dedicatedColors: metaQuery.data?.dedicatedColors ?? [],
    baseExcludeColors: metaQuery.data?.baseExcludeColors ?? [],
    baseTimetableByGrade,
    annualProjectPrograms: annualProjectProgramsQuery.data ?? [],
    annualRowsByTerm: {
      1: term1Bundle.timetableQuery.data ?? [],
      2: term2Bundle.timetableQuery.data ?? [],
    },
    progressRowsByTerm: {
      1: term1Bundle.progressQuery.data ?? [],
      2: term2Bundle.progressQuery.data ?? [],
    },
    calendarDaysByTerm,
    savedNotesByTerm: {
      1: term1Bundle.notesQuery.data ?? [],
      2: term2Bundle.notesQuery.data ?? [],
    },
    scopedRowsByTerm: {
      1: term1Bundle.scopedRowsQuery.data ?? [],
      2: term2Bundle.scopedRowsQuery.data ?? [],
    },
    fixedContentRowsByTerm: {
      1: term1Bundle.fixedRowsQuery.data ?? [],
      2: term2Bundle.fixedRowsQuery.data ?? [],
    },
    savedTrackColorMap: trackColorsQuery.data ?? {},
    nonClassHolidaySetByTerm,
    calendarDayKindByTerm,
    offDayByDateByTerm,
    loading,
    notesLoading,
    error,
  };
}
